import { useUserManagementServiceV1 } from "@rw1/api-services";
import { UserManagement_V1 } from "@rw1/auto-generated-clients";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { ASSIGNED_VIA_DIRECT } from "../../../components/AssignedViaCell";
import { AssignedVia } from "../../../components/ManageAccessDialog";
import { useAllExperiences } from "../../../hooks/useAllExperiences";

export interface UserExperienceRow {
  id: string;
  title: string;
  companyName: string;
  assignedVia: AssignedVia[];
  contentMetadataId: string;
  expirationDate?: Date;
}
interface UseUserExperiencesParams {
  userId: string;
  enabled?: boolean;
  userGroups: UserManagement_V1.UserGroup[];
}

interface UseUserExperiencesReturn {
  userExperiences: UserExperienceRow[];
  isLoading: boolean;
}

export const USER_RELEASE_CHANNELS_QUERY_KEY = "userReleaseChannels";

const mapUSerGroupIdToName = (
  record: Record<string, string>,
  id: string | undefined,
): string | undefined => {
  if (!id) return ASSIGNED_VIA_DIRECT;

  return record[id];
};

export const useUserExperiences = ({
  userId,
  enabled = true,
  userGroups,
}: UseUserExperiencesParams): UseUserExperiencesReturn => {
  const userManagementClient = useUserManagementServiceV1();

  const { data: userReleaseChannels, isLoading } = useQuery({
    queryKey: [USER_RELEASE_CHANNELS_QUERY_KEY, userId],
    queryFn: () =>
      userManagementClient.getReleaseChannelsForUser(userId, 0, 1000),
    enabled: enabled && !!userId,
  });

  const { experiencesMap: allExperiencesMap } = useAllExperiences();

  const userExperiences = useMemo(() => {
    if (!userReleaseChannels) {
      return [];
    }

    const userGroupNamesRecord = userGroups.reduce<Record<string, string>>(
      (acc, group) => {
        acc[group.userGroupId] = group.userGroupName;

        return acc;
      },
      {},
    );

    const experiencesMap = new Map<string, UserExperienceRow>();

    for (const urc of userReleaseChannels) {
      const existing = experiencesMap.get(urc.releaseChannelId);

      const source = mapUSerGroupIdToName(
        userGroupNamesRecord,
        urc.userGroupId,
      );

      if (!source) {
        continue;
      }

      const assignedVia = {
        source,
        sourceId: urc.userGroupId ?? "",
      };

      const expirationDate = urc.expiresAt;

      if (existing) {
        existing.assignedVia.push(assignedVia);

        const isExpirationDateLater =
          !expirationDate ||
          (existing.expirationDate && existing.expirationDate < expirationDate);

        if (isExpirationDateLater) {
          existing.expirationDate = expirationDate;
        }
      } else {
        const releaseChannel = allExperiencesMap.get(urc.releaseChannelId);

        if (releaseChannel) {
          const { friendlyName, companyName, contentMetadataId } =
            releaseChannel;

          experiencesMap.set(urc.releaseChannelId, {
            id: urc.releaseChannelId,
            title: friendlyName,
            companyName: companyName ?? "",
            assignedVia: [assignedVia],
            expirationDate,
            contentMetadataId,
          });
        }
      }
    }

    return Array.from(experiencesMap.values());
  }, [userReleaseChannels, userGroups, allExperiencesMap]);

  return {
    userExperiences,
    isLoading,
  };
};
